import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/Shell";
import LevelCard from "../components/LevelCard";
import { api, SUBJECTS, getUser } from "../lib/api";
import { Calculator, BookOpen, GlobeHemisphereWest, Flask, MapTrifold, ChatCircleText, Translate, BookBookmark, CameraPlus, Sparkle, TrendUp, CheckCircle, Trophy } from "@phosphor-icons/react";

const ICONS = {
  matematik: Calculator,
  turkce: BookOpen,
  ingilizce: GlobeHemisphereWest,
  fen: Flask,
  sosyal: MapTrifold,
  almanca: ChatCircleText,
  fransizca: Translate,
  italyanca: BookBookmark,
};

export default function Dashboard() {
  const nav = useNavigate();
  const user = getUser();
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get("/me/weekly-summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/me/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  return (
    <Shell title={`Merhaba, ${user?.child_name || "Öğrenci"}`}>
      <section className="mb-6">
        <h1 className="font-outfit font-extrabold text-3xl sm:text-4xl tracking-tight text-brand-ink">
          {user?.child_name && user.child_name !== "Öğrenci"
            ? `${user.child_name}, bugün hangi dersle başlıyoruz?`
            : "Bugün hangi dersle başlıyoruz?"}
        </h1>
        <p className="font-nunito text-brand-ink2 mt-2">
          Bir ders seç, ödevinin fotoğrafını çek — bakalım konuyu ne kadar öğrenmişsin.
        </p>
      </section>

      <button
        data-testid="quick-upload-cta"
        onClick={() => {
          const last = localStorage.getItem("evodevi_last_subject");
          nav(last ? `/app/upload?subject=${last}` : "/app/upload");
        }}
        className="w-full bg-brand-ink text-white rounded-3xl p-5 mb-5 flex items-center gap-4 shadow-lg active:scale-[0.99] transition-transform"
      >
        <div className="w-14 h-14 rounded-2xl bg-brand-primary flex items-center justify-center">
          <CameraPlus size={28} weight="duotone" color="#1D3557" />
        </div>
        <div className="text-left flex-1">
          <div className="font-outfit font-bold text-lg leading-tight">Ödev fotoğrafı yükle</div>
          <div className="font-nunito text-sm text-white/70">Birden fazla sayfa olabilir.</div>
        </div>
        <Sparkle size={22} weight="fill" color="#FFB703" />
      </button>

      {progress && <div className="mb-5"><LevelCard progress={progress} compact /></div>}

      {summary && (summary.submissions_count > 0 || summary.tests_count > 0) && (
        <section className="mb-7 bg-white rounded-3xl p-5 shadow-sm border-l-4 border-brand-primary" data-testid="weekly-summary-card">
          <div className="flex items-center gap-2 mb-3">
            <TrendUp size={22} weight="duotone" color="#2A9D8F" />
            <div className="font-outfit font-extrabold text-brand-ink text-lg">Bu haftan</div>
          </div>
          <p className="font-nunito text-brand-ink2 text-sm mb-4" data-testid="weekly-message">{summary.message}</p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="rounded-2xl bg-brand-paperAlt p-3 text-center">
              <div className="font-outfit font-extrabold text-2xl text-brand-ink" data-testid="weekly-subs-count">{summary.submissions_count}</div>
              <div className="text-[11px] font-nunito text-brand-ink2">Ödev</div>
            </div>
            <div className="rounded-2xl bg-good-bg p-3 text-center">
              <div className="font-outfit font-extrabold text-2xl text-good" data-testid="weekly-accuracy">
                {summary.accuracy_pct != null ? `%${summary.accuracy_pct}` : "—"}
              </div>
              <div className="text-[11px] font-nunito text-brand-ink2">Doğruluk</div>
            </div>
            <div className="rounded-2xl bg-brand-primary/20 p-3 text-center">
              <div className="font-outfit font-extrabold text-2xl text-brand-ink" data-testid="weekly-avg-test">
                {summary.avg_test_score != null ? `${summary.avg_test_score}/10` : "—"}
              </div>
              <div className="text-[11px] font-nunito text-brand-ink2">Ort. test</div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap text-xs font-nunito">
            <span className="inline-flex items-center gap-1 text-good"><CheckCircle size={14} weight="fill" /> {summary.correct} doğru</span>
            <span className="inline-flex items-center gap-1 text-bad">· {summary.incorrect} yanlış</span>
            <span className="inline-flex items-center gap-1 text-blank">· {summary.blank} boş</span>
            <span className="inline-flex items-center gap-1 text-brand-ink2">· {summary.tests_count} <Trophy size={12} weight="fill" color="#FFB703" /></span>
          </div>
        </section>
      )}

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-outfit font-bold text-xl text-brand-ink">Dersler</h2>
        <span className="text-xs uppercase tracking-widest font-nunito font-semibold text-brand-ink2">{SUBJECTS.length} ders</span>
      </div>

      <div className="grid grid-cols-2 gap-3" data-testid="subjects-grid">
        {SUBJECTS.map((s) => {
          const Icon = ICONS[s.key] || BookOpen;
          return (
            <button
              key={s.key}
              data-testid={`subject-card-${s.key}`}
              onClick={() => nav(`/app/upload?subject=${s.key}`)}
              className="flex flex-col items-start p-5 bg-white rounded-3xl border-2 border-transparent shadow-sm hover:border-brand-primary/50 active:scale-95 transition-all text-left"
            >
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ backgroundColor: s.bg }}>
                <Icon size={26} weight="duotone" color={s.color} />
              </div>
              <div className="font-outfit font-bold text-base text-brand-ink">{s.name}</div>
              <div className="font-nunito text-xs text-brand-ink2 mt-1">Ödev yükle →</div>
            </button>
          );
        })}
      </div>
    </Shell>
  );
}
